/**
 * I18n Provider
 * Shared internationalization provider for all portals
 * Handles language switching, RTL, and URL persistence
 */

import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { I18nextProvider } from 'react-i18next'
import type { i18n as I18nInstance } from 'i18next'
import { isRTLLanguage } from '../utils/rtl'

// ============================================================================
// Types
// ============================================================================

export interface I18nProviderProps {
  children: ReactNode
  i18n: I18nInstance
  /**
   * Additional wrapper component (e.g., LocaleProvider from @spcc/i18n)
   */
  wrapper?: React.ComponentType<any>
}

// ============================================================================
// Provider Component
// ============================================================================

export function I18nProvider({ children, i18n, wrapper: Wrapper }: I18nProviderProps) {
  const [isInitialized, setIsInitialized] = useState(i18n.isInitialized)

  // Wait for i18n initialization
  useEffect(() => {
    if (i18n.isInitialized) {
      setIsInitialized(true)
      return
    }

    const handleInitialized = () => setIsInitialized(true)
    const handleLanguageChanged = () => {
      if (i18n.isInitialized) {
        setIsInitialized(true)
      }
    }

    i18n.on('initialized', handleInitialized)
    i18n.on('languageChanged', handleLanguageChanged)

    if (i18n.isInitialized) {
      setIsInitialized(true)
    }

    return () => {
      i18n.off('initialized', handleInitialized)
      i18n.off('languageChanged', handleLanguageChanged)
    }
  }, [i18n])

  // Handle language from URL parameter on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search)
      const urlLang = urlParams.get('lang')
      // Allow any valid language code from URL
      if (urlLang && urlLang !== i18n.language) {
        i18n.changeLanguage(urlLang)
      }
    }
  }, [i18n])

  // Handle language changes (RTL, persistence, URL)
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      const isRTL = isRTLLanguage(lng)
      document.documentElement.dir = isRTL ? 'rtl' : 'ltr'
      document.documentElement.lang = lng

      // Persist to localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem('i18nextLng', lng)
        localStorage.setItem('locale', lng)

        // Update URL without page reload
        const url = new URL(window.location.href)
        if (url.searchParams.get('lang') !== lng) {
          url.searchParams.set('lang', lng)
          window.history.replaceState({}, '', url.toString())
        }
      }
    }

    handleLanguageChange(i18n.language)
    i18n.on('languageChanged', handleLanguageChange)

    return () => {
      i18n.off('languageChanged', handleLanguageChange)
    }
  }, [i18n])

  // Prevent flash of unstyled content
  if (!isInitialized) {
    return null
  }

  const content = <I18nextProvider i18n={i18n}>{children}</I18nextProvider>

  // Wrap with additional provider if provided
  const AnyWrapper = Wrapper as any; return AnyWrapper ? <AnyWrapper>{content}</AnyWrapper> : content
}
